import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../components/icons';
import { Avatar, Badge, Button, Card, CopyButton, Field, IconButton, Input, NumberField, PageHeader, Segmented, Select, cx } from '../components/ui';
import { api, type JobSummary } from '../lib/api';

/**
 * Recruiter scheduling: the weekly availability candidates can book into
 * (globally or per job), and the interviews booked for the next two weeks.
 */

interface AvailabilityWindow {
  id?: string;
  weekday: number;
  startMinute: number;
  endMinute: number;
  timezone: string;
  slotMinutes: number;
  capacity: number;
}

interface BookedInterview {
  id: string;
  stage: 'SCREENING' | 'ASSESSMENT' | 'RECRUITER';
  scheduledAt: string;
  status: string;
  channel: 'WEB' | 'VIDEO' | 'PHONE';
  rescheduleCount: number;
  link: string | null;
  candidate: { id: string; name: string; email: string | null; phoneE164: string | null; job: { id: string; title: string } };
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIMEZONES = ['Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Europe/London', 'America/New_York', 'UTC'];

const toHHMM = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const fromHHMM = (s: string) => {
  const [h, m] = s.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** AI: the AI interview and the skills assessment (one AI interviewer). RECRUITER: when recruiters take their own interviews. */
type Kind = 'AI' | 'RECRUITER';
const STAGE_CHIP: Record<BookedInterview['stage'], string> = { SCREENING: 'AI interview', ASSESSMENT: 'Skills assessment', RECRUITER: 'Recruiter interview' };

export function SchedulerPage() {
  const qc = useQueryClient();
  const [jobId, setJobId] = useState<string>('');
  const [kind, setKind] = useState<Kind>('AI');
  const jobs = useQuery({ queryKey: ['jobs'], queryFn: () => api.get<JobSummary[]>('/api/jobs') });
  const availability = useQuery({
    queryKey: ['availability', jobId, kind],
    queryFn: () => api.get<AvailabilityWindow[]>(`/api/scheduling/availability?kind=${kind}${jobId ? `&jobId=${encodeURIComponent(jobId)}` : ''}`),
  });
  const bookings = useQuery({
    queryKey: ['bookings'],
    queryFn: () => api.get<BookedInterview[]>('/api/scheduling/bookings?days=14'),
    refetchInterval: 30_000,
  });

  const [draft, setDraft] = useState<AvailabilityWindow[]>([]);
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!availability.data) return;
    setDraft(availability.data);
    setTimezone(availability.data[0]?.timezone ?? 'Asia/Kolkata');
    setSaved(false);
  }, [availability.data]);

  const save = useMutation({
    mutationFn: () =>
      api.put<AvailabilityWindow[]>('/api/scheduling/availability', {
        jobId: jobId || null,
        kind,
        windows: draft.map(({ weekday, startMinute, endMinute, slotMinutes, capacity }) => ({ weekday, startMinute, endMinute, slotMinutes, capacity, timezone })),
      }),
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['availability', jobId, kind] });
    },
  });

  const update = (idx: number, patch: Partial<AvailabilityWindow>) => {
    setSaved(false);
    setDraft((d) => d.map((w, i) => (i === idx ? { ...w, ...patch } : w)));
  };
  const add = (weekday: number) => {
    setSaved(false);
    setDraft((d) => [...d, { weekday, startMinute: 10 * 60, endMinute: 13 * 60, timezone, slotMinutes: kind === 'RECRUITER' ? 45 : 30, capacity: 1 }]);
  };
  const remove = (idx: number) => {
    setSaved(false);
    setDraft((d) => d.filter((_, i) => i !== idx));
  };
  const copyMondayToWeekdays = () => {
    setSaved(false);
    setDraft((d) => {
      const monday = d.filter((w) => w.weekday === 1);
      return [...d.filter((w) => w.weekday === 1 || w.weekday > 5), ...[2, 3, 4, 5].flatMap((weekday) => monday.map((w) => ({ ...w, id: undefined, weekday })))];
    });
  };

  const byDay = useMemo(() => {
    const map = new Map<string, BookedInterview[]>();
    for (const b of bookings.data ?? []) {
      const k = new Date(b.scheduledAt).toLocaleDateString('en-IN', { timeZone: timezone, weekday: 'long', day: 'numeric', month: 'long' });
      map.set(k, [...(map.get(k) ?? []), b]);
    }
    return [...map.entries()];
  }, [bookings.data, timezone]);

  const invalid = draft.some((w) => w.endMinute <= w.startMinute || w.endMinute - w.startMinute < w.slotMinutes);
  const hoursAWeek = draft.reduce((n, w) => n + Math.max(0, w.endMinute - w.startMinute), 0) / 60;

  return (
    <>
      <PageHeader
        eyebrow="Scheduler"
        title="When candidates can book"
        subtitle="The AI interviewer takes one interview or skills assessment at a time; recruiter interviews use the hours your recruiters set. For the AI rounds, booking is a reminder, not a gate: candidates can still start any time before their link expires."
        actions={
          <Segmented<Kind>
            label="Whose availability"
            size="md"
            value={kind}
            onChange={setKind}
            options={[
              { value: 'AI', label: 'AI interviews & assessments', icon: 'headphones' },
              { value: 'RECRUITER', label: 'Recruiter interviews', icon: 'users' },
            ]}
          />
        }
      />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <Card
          title="Weekly hours"
          description={`${hoursAWeek % 1 === 0 ? hoursAWeek : hoursAWeek.toFixed(1)} hours a week, in ${timezone}.`}
          actions={
            <>
              {saved && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
                  <Icon name="check" className="h-3.5 w-3.5" /> Saved
                </span>
              )}
              {save.error && <span className="text-xs text-destructive">{(save.error as Error).message}</span>}
              <Button variant="primary" size="sm" icon="check" disabled={invalid} loading={save.isPending} onClick={() => save.mutate()}>
                Save availability
              </Button>
            </>
          }
        >
          <div className="grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <Field label="Applies to">
              <Select value={jobId} onChange={(e) => setJobId(e.target.value)}>
                <option value="">All jobs (default)</option>
                {jobs.data?.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.title}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Time zone">
              <Select
                value={timezone}
                onChange={(e) => {
                  setTimezone(e.target.value);
                  setSaved(false);
                }}
              >
                {[...new Set([timezone, ...TIMEZONES])].map((tz) => (
                  <option key={tz}>{tz}</option>
                ))}
              </Select>
            </Field>
            <Button icon="copy" onClick={copyMondayToWeekdays}>
              Copy Monday to Tue–Fri
            </Button>
          </div>
          {jobId && availability.data?.length === 0 && <p className="mt-3 text-sm text-muted-fg">This job has no availability of its own, so it uses the default for all jobs. Add hours here to override it.</p>}
          {!jobId && availability.data?.length === 0 && (
            <p className="mt-3 text-sm text-muted-fg">
              No availability yet: candidates are offered 30-minute slots inside the calling hours from Settings
              {kind === 'RECRUITER' ? ", one at a time. Add your recruiters' hours here." : '.'}
            </p>
          )}

          <div className="mt-5 divide-y divide-border">
            {WEEKDAYS.map((name, i) => {
              const weekday = i + 1;
              const rows = draft.map((w, idx) => ({ w, idx })).filter(({ w }) => w.weekday === weekday);
              return (
                <div key={name} className="flex flex-wrap items-start gap-4 py-3.5">
                  <div className="w-24 pt-2 text-sm font-semibold text-fg">{name}</div>
                  <div className="min-w-0 flex-1 space-y-2">
                    {rows.length === 0 && <p className="pt-2 text-sm text-faint">Unavailable</p>}
                    {rows.map(({ w, idx }) => {
                      const bad = w.endMinute <= w.startMinute;
                      return (
                        <div key={idx} className={cx('flex flex-wrap items-center gap-2 rounded-md bg-surface-2 p-2 ring-1', bad ? 'ring-destructive/40' : 'ring-border')}>
                          <Input type="time" className="!w-32" value={toHHMM(w.startMinute)} onChange={(e) => update(idx, { startMinute: fromHHMM(e.target.value) })} aria-label="From" />
                          <span className="text-xs text-faint">to</span>
                          <Input type="time" className="!w-32" value={toHHMM(Math.min(w.endMinute, 1439))} onChange={(e) => update(idx, { endMinute: fromHHMM(e.target.value) })} aria-label="To" />
                          <span className="w-36">
                            <Select value={w.slotMinutes} onChange={(e) => update(idx, { slotMinutes: Number(e.target.value) })} aria-label="Slot length">
                              {[15, 20, 30, 45, 60].map((m) => (
                                <option key={m} value={m}>
                                  {m}-min slots
                                </option>
                              ))}
                            </Select>
                          </span>
                          <span className="w-36">
                            <NumberField value={w.capacity} min={1} max={100} suffix="at a time" onChange={(v) => update(idx, { capacity: v })} aria-label="Interviews at a time" />
                          </span>
                          <IconButton label="Remove" icon="trash" size="sm" className="ml-auto hover:!text-destructive" onClick={() => remove(idx)} />
                          {bad && <span className="w-full text-xs text-destructive">End must be after start</span>}
                        </div>
                      );
                    })}
                  </div>
                  <Button size="sm" variant="soft" icon="plus" onClick={() => add(weekday)} className="mt-0.5">
                    Add hours
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>

        <Card title="Booked interviews" description="The next 14 days." className="xl:self-start">
          {bookings.isLoading && <p className="text-sm text-muted-fg">Loading…</p>}
          {bookings.data?.length === 0 && <p className="text-sm text-muted-fg">No bookings yet. Candidates book from the link in their invite email.</p>}
          <div className="space-y-5">
            {byDay.map(([day, rows]) => (
              <div key={day}>
                <h3 className="eyebrow mb-2">{day}</h3>
                <ul className="divide-y divide-border overflow-hidden rounded-md ring-1 ring-border">
                  {rows.map((b) => (
                    <li key={b.id} className="flex flex-wrap items-center gap-3 bg-surface px-3 py-2.5 text-sm">
                      <span className="w-16 font-mono text-xs tabular text-fg">{new Date(b.scheduledAt).toLocaleTimeString('en-IN', { timeZone: timezone, hour: 'numeric', minute: '2-digit' })}</span>
                      <Avatar name={b.candidate.name} size="xs" />
                      <span className="min-w-0 flex-1">
                        <Link to={`/candidates/${b.candidate.id}`} className="block truncate font-semibold text-fg hover:text-accent">
                          {b.candidate.name}
                        </Link>
                        <span className="block truncate text-xs text-muted-fg">{b.candidate.job.title}</span>
                      </span>
                      <Badge tone="slate" size="xs">
                        {STAGE_CHIP[b.stage] ?? (b.channel === 'VIDEO' ? 'Video' : 'Web')}
                      </Badge>
                      <Badge tone="blue" size="xs">
                        {b.status.replace('_', ' ').toLowerCase()}
                      </Badge>
                      {b.rescheduleCount > 0 && <span className="text-xs text-warning">moved {b.rescheduleCount}×</span>}
                      {b.link && <CopyButton text={b.link} />}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}
